/*
  Keeps the frame colour named in the frame note in step with the picker.

  The note is rendered in Liquid, so it is correct on page load and then
  goes stale: Dawn updates price, SKU and buy buttons on a variant change
  but leaves custom blocks alone. Without this, someone selecting Oak is
  told their print ships in black, which is worse than saying nothing.

  Reads the selection straight from the DOM rather than subscribing to
  Dawn's pubsub, so it does not depend on script load order. Dawn puts
  data-option-name on every variant input, and on each option inside the
  select fallback, so both pickers are covered.

  Markup contract:
    <ironwood-frame-note data-option-name="Frame colour">
      ... <span data-frame-name>black</span> ...
    </ironwood-frame-note>
*/

class IronwoodFrameNote extends HTMLElement {
  connectedCallback() {
    this.target = this.querySelector('[data-frame-name]');
    this.optionName = (this.dataset.optionName || '').toLowerCase();
    if (!this.target || !this.optionName) return;

    // Scope to the product section so a second product on the page cannot
    // drive this note. Falls back outward if the expected wrapper is absent.
    this.scope = this.closest('product-info') || this.closest('.section') || document;

    this.handleChange = this.handleChange.bind(this);
    this.scope.addEventListener('change', this.handleChange);
    this.update();
  }

  disconnectedCallback() {
    this.scope?.removeEventListener('change', this.handleChange);
  }

  handleChange(event) {
    if (event.target.closest('variant-selects')) this.update();
  }

  matchesOption(el) {
    return (el.dataset.optionName || '').toLowerCase() === this.optionName;
  }

  selectedValue() {
    // Pill and swatch pickers render radio inputs.
    const radios = this.scope.querySelectorAll('input[type="radio"][data-option-name]:checked');
    for (const input of radios) {
      if (this.matchesOption(input)) return input.value;
    }

    // Dropdown fallback carries the dataset on each option.
    for (const select of this.scope.querySelectorAll('select')) {
      const option = select.options[select.selectedIndex];
      if (option && this.matchesOption(option)) return option.value;
    }

    return null;
  }

  update() {
    const value = this.selectedValue();
    if (value) this.target.textContent = value.toLowerCase();
  }
}

if (!customElements.get('ironwood-frame-note')) {
  customElements.define('ironwood-frame-note', IronwoodFrameNote);
}
